import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Linkedin, Mail, UserRound, IdCard } from "lucide-react";
import SEO from "../components/site/SEO";
import SectionHeader from "../components/site/SectionHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEAM_IMG =
  "https://images.unsplash.com/photo-1633457897190-8c8c23a12c4c?auto=format&fit=crop&w=1800&q=80";

const VALUES = [
  {
    n: "01",
    title: "Operator-First Mindset",
    body: "We staff with practitioners — not generalists. Every analyst has handled real incidents under real pressure.",
  },
  {
    n: "02",
    title: "Outcome Over Output",
    body: "Reports don't matter if attackers still get in. We measure ourselves on detection time and contained incidents.",
  },
  {
    n: "03",
    title: "Transparency Without Compromise",
    body: "You see what we see. Live dashboards, raw evidence, and clear post-incident retrospectives.",
  },
  {
    n: "04",
    title: "Built to Scale With You",
    body: "From mid-market to global enterprise — coverage scales as your attack surface grows.",
  },
];

export default function About() {
  const [directors, setDirectors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/directors`);
        setDirectors(data);
      } catch {
        setDirectors([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  return (
    <div data-testid="about-page" className="bg-[#050507] text-white">
      <SEO
        title="About Quasect — Operator-Grade SOC Level 2 Cybersecurity"
        description="Founded by operators with 40+ years of combined cybersecurity experience. Meet the team building Quasect's SOC Level 2 services."
        path="/about"
      />

      {/* HERO */}
      <section className="relative overflow-hidden noise border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src={TEAM_IMG}
            alt="Quasect IT security team in office"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/85 to-[#050507]" />
          <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-20 md:pt-32 md:pb-28">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3">
            <span className="w-6 h-px bg-[#39FF14]" /> ABOUT QUASECT
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="about-headline"
            className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] tracking-tighter leading-[1.02] max-w-5xl break-words"
          >
            Cybersecurity built by people who've{" "}
            <span className="text-[#39FF14]">defended at scale</span>.
          </motion.h1>
          <p className="mt-7 text-lg md:text-xl text-[#8B93A5] max-w-3xl leading-relaxed">
            Quasect was founded on a simple thesis: most enterprise security failures aren't
            tooling problems — they're operator problems. We built a Level 2 SOC staffed and led
            by people who've spent careers on the frontline.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: "40+", l: "Years combined experience" },
              { v: "L2", l: "SOC tier classification" },
              { v: "24/7", l: "Continuous coverage" },
              { v: "5+", l: "Compliance frameworks" },
            ].map((s) => (
              <div key={s.l} className="border-l border-white/10 pl-4">
                <div className="font-display font-bold text-2xl md:text-3xl">{s.v}</div>
                <div className="font-mono text-[10px] text-[#8B93A5] uppercase tracking-wider mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECTORS */}
      <section data-testid="founders-section" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            overline="The Leadership"
            title="40+ years of combined industry experience."
            subtitle="The Quasect leadership team brings real operator discipline to enterprise cybersecurity."
            testId="founders-header"
          />

          {loaded && directors.length === 0 ? (
            <div className="mt-14 border border-dashed border-white/10 py-20 text-center text-[#8B93A5]">
              Leadership profiles will be published shortly.
            </div>
          ) : (
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {directors.map((f) => (
                <article
                  key={f.id}
                  data-testid={`founder-${f.id}`}
                  className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 transition-all overflow-hidden"
                >
                  <div className="aspect-[4/5] overflow-hidden relative bg-black">
                    {f.image_data_url ? (
                      <img
                        src={f.image_data_url}
                        alt={`${f.name} — ${f.role} at Quasect`}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-[#2a2d36]">
                        <UserRound className="w-20 h-20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0D] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                      {f.role}
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display font-bold text-2xl tracking-tight">
                      {f.name}
                    </h3>
                    <p className="mt-4 text-[#8B93A5] leading-relaxed text-sm">{f.bio}</p>
                    <div className="mt-6 flex gap-2 flex-wrap">
                      {f.slug && (
                        <Link
                          to={`/card/${f.slug}`}
                          data-testid={`about-card-link-${f.slug}`}
                          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-wider uppercase border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 px-3 py-1.5 transition-colors"
                        >
                          <IdCard className="w-3 h-3" /> View Card
                        </Link>
                      )}
                      {f.linkedin ? (
                        <a
                          href={f.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-wider uppercase border border-white/15 hover:border-white text-[#8B93A5] hover:text-white px-3 py-1.5"
                        >
                          <Linkedin className="w-3 h-3" /> LinkedIn
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-wider text-[#8B93A5] uppercase border border-white/15 px-3 py-1.5">
                          <Linkedin className="w-3 h-3" /> LinkedIn
                        </span>
                      )}
                      {f.email ? (
                        <a
                          href={`mailto:${f.email}`}
                          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-wider uppercase border border-white/15 hover:border-white text-[#8B93A5] hover:text-white px-3 py-1.5"
                        >
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-wider text-[#8B93A5] uppercase border border-white/15 px-3 py-1.5">
                          <Mail className="w-3 h-3" /> Direct
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#08080A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <SectionHeader
              overline="What we believe"
              title="Operator discipline. No vendor theatre."
              subtitle="Four operating principles that shape every engagement we take."
            />
          </div>
          <div className="lg:col-span-7 space-y-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.n}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.07 }}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#39FF14]/50 p-7 md:p-9 flex gap-6 transition-all"
                data-testid={`value-card-${i}`}
              >
                <div className="font-display font-black text-3xl md:text-4xl text-[#39FF14]/30 group-hover:text-[#39FF14] transition-colors">
                  {v.n}
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl md:text-2xl">{v.title}</h3>
                  <p className="mt-3 text-[#8B93A5] leading-relaxed">{v.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="border border-white/10 bg-[#0A0A0D] p-10 md:p-16 grid lg:grid-cols-12 gap-8 items-center noise relative">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
            <div className="lg:col-span-8">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#00F0FF] flex items-center gap-3">
                <ShieldCheck className="w-3.5 h-3.5" /> WORK WITH US
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mt-5 tracking-tight leading-[1.05]">
                Ready to upgrade your <span className="text-[#39FF14]">defense posture?</span>
              </h2>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta"
              className="btn-sweep bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm flex items-center justify-center gap-2"
            >
              Talk to a SOC Lead <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
